'use client';

import React, { useState, useEffect, useRef } from 'react';
import { useSupabaseAuth } from '@/lib/auth/simplified/SupabaseAuthContext';
import { cn } from '@/lib/utils';
import { getRoomUrl } from '@/lib/utils/room-url';
import Link from 'next/link';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { 
  Search, 
  Bell, 
  MessageCircle, 
  Video,
  Menu,
  Home,
  LayoutDashboard,
  User,
  Settings,
  LogOut,
  Sparkles 
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuGroup,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { usePathname, useRouter } from 'next/navigation';
import DashboardNav from '@/app/dashboard/(standard)/dashboard-nav';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Shield } from 'lucide-react';
import DashboardSearchBar from '@/components/search/DashboardSearchBar';
import dynamic from 'next/dynamic';
import { ProcessingStatusBell } from '@/components/processing-status-bell';

const PublicSearchBar = dynamic(() => import('@/components/public/PublicSearchBar'), {
  ssr: false
});

interface AppHeaderProps {
  variant: 'default' | 'dashboard' | 'public';
  showNav?: boolean;
  showSearch?: boolean;
  showMenu?: boolean;
  landingMode?: boolean;
  className?: string;
  leftContent?: React.ReactNode;
  rightContent?: React.ReactNode;
  navItems?: { name: string; href: string }[];
}

/**
 * Unified header component that handles all header variants consistently.
 * Always accesses auth context but conditionally renders auth UI based on variant.
 */
export function AppHeader({
  variant = 'default',
  showNav = true,
  showSearch = variant === 'dashboard',
  showMenu = variant === 'dashboard',
  landingMode = false,
  className,
  leftContent,
  rightContent,
  navItems
}: AppHeaderProps) {
  // Always access auth context for consistency across all routes
  const { 
    isAuthenticated, 
    profile, 
    signOut, 
    status,
    user, 
    refreshProfile,
    isLoading 
  } = useSupabaseAuth();
  
  // For development debugging
  const showDebugInfo = process.env.NODE_ENV === 'development';
  const pathname = usePathname();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAdminLoading, setIsAdminLoading] = useState(true);
  const [adminCheckComplete, setAdminCheckComplete] = useState(false);
  const router = useRouter();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Debug the auth state
  useEffect(() => {
    if (showDebugInfo) {
      console.log('[AppHeader] Auth state:', {
        isAuthenticated,
        hasProfile: !!profile,
        profileHandle: profile?.handle,
        isLoading,
        variant,
        rightContent: !!rightContent
      });
    }
  }, [isAuthenticated, profile, isLoading, variant, rightContent, showDebugInfo]);
  
  // Reset admin check when user changes
  useEffect(() => {
    if (!user) {
      setAdminCheckComplete(false);
      setIsAdmin(false);
      setIsAdminLoading(false);
    } else {
      // User changed, reset the check
      setAdminCheckComplete(false);
    }
  }, [user?.id]); // Only depend on user.id to avoid unnecessary resets

  // Check admin status
  useEffect(() => {
    // Skip if no user or still loading
    if (!user || isLoading) {
      return;
    }

    // Skip if already checked for this user
    if (adminCheckComplete) {
      return;
    }

    const checkAdminStatus = async () => {
      console.log('[AppHeader] Starting admin check for user:', user.id);
      setIsAdminLoading(true);
      setAdminCheckComplete(true); // Mark as complete immediately to prevent duplicate checks

      try {
        const { createBrowserClient } = await import('@/lib/supabase-clients');
        const supabase = createBrowserClient();
        
        // First, get the user profile to get the user_id
        console.log('[AppHeader] Fetching user profile for auth_id:', user.id);
        const { data: userProfile, error: profileError } = await supabase
          .from('users')
          .select('id')
          .eq('auth_id', user.id)
          .single();
          
        if (profileError || !userProfile) {
          console.error('[AppHeader] Failed to get user profile:', profileError);
          setIsAdmin(false);
          setIsAdminLoading(false);
          return;
        }
        
        console.log('[AppHeader] Got user profile, user_id:', userProfile.id);
        
        // Now check if user is admin
        const { data: isAdminResult, error: adminError } = await supabase
          .rpc('is_admin_user', { p_user_id: userProfile.id });
          
        console.log('[AppHeader] Admin check result:', { 
          isAdmin: isAdminResult, 
          error: adminError 
        });
        
        setIsAdmin(!!isAdminResult);
      } catch (error) {
        console.error('[AppHeader] Error in admin check:', error);
        setIsAdmin(false);
      } finally {
        setIsAdminLoading(false);
        console.log('[AppHeader] Admin check complete');
      }
    };

    checkAdminStatus();
  }, [user?.id, isLoading]); // Only depend on user.id and isLoading
  
  // Simplify state management - use Supabase's auth status directly
  // Following Supabase patterns, we'll derive UI state from auth context
  // rather than maintaining additional state that might get out of sync
  const profileReady = !!profile || (isAuthenticated && !!user?.email);
  
  // Show loading state if auth or admin status is still loading
  // But for dashboard variant, show the header structure immediately
  const isStillLoading = isLoading || (isAuthenticated && isAdminLoading);
  
  // For dashboard pages, show the header structure immediately
  // to prevent layout shifts and missing headers
  if (variant === 'dashboard') {
    // Continue with normal rendering even if loading
  } else if (isStillLoading && !rightContent) {
    return (
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto">
          <div className="flex h-14 items-center justify-between px-4">
            <div className="flex items-center space-x-4">
              <Link href="/" className="flex items-center space-x-2">
                <span className="text-xl font-bold">ApprendoTalks</span>
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              <ThemeToggle />
              <Skeleton className="h-8 w-8 rounded-full" />
            </div>
          </div>
        </div>
      </header>
    );
  }
  
  // Let the auth context handle profile refreshing - we'll just provide nice loading states
  // No need to force profile refresh from the header component
  // Following Supabase patterns by trusting the auth context to manage state
  
  // Generate user initials for avatar fallback
  const getUserInitials = () => {
    try {
      if (profile?.name) {
        const nameParts = profile.name.split(' ');
        if (nameParts.length === 1) return nameParts[0].charAt(0).toUpperCase();
        
        return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
      }
      
      if (user?.email) {
        const emailParts = user.email.split('@');
        return emailParts[0].charAt(0).toUpperCase();
      }
      
      return 'U';
    } catch (e) {
      console.error('[AppHeader] Error generating user initials:', e);
      return 'U';
    }
  };
  
  return (
    <TooltipProvider>
      <div className="header-container">
        <header className={cn(
          "sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60",
          className
        )}>
          <div className="container mx-auto px-4">
            <div className="flex h-14 items-center justify-between gap-4">
            {/* Left section */}
            <div className="flex items-center gap-6">
              {leftContent || (
                <>
                  {variant !== 'dashboard' && (
                    <Link href="/" className="flex items-center gap-2.5">
                      <div className="w-8 h-8 bg-[#EF4444] rounded-lg flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                        a
                      </div>
                      <span className="font-bold text-xl tracking-tight text-foreground hidden sm:inline">
                        Apprendo
                      </span>
                    </Link>
                  )}
                </>
              )}

              {showNav && variant !== 'dashboard' && (
                <nav className="hidden md:flex items-center gap-1">
                  {landingMode && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (pathname === '/') {
                            document.querySelector('#how-it-works')?.scrollIntoView({ behavior: 'smooth' });
                          } else {
                            router.push('/#how-it-works');
                          }
                        }}
                      >
                        How It Works
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => router.push('/pricing')}
                      >
                        Pricing
                      </Button>
                    </>
                  )}
                  {!landingMode && (
                    <Button
                      variant={pathname?.startsWith('/home') ? 'secondary' : 'ghost'}
                      size="sm"
                      onClick={() => router.push('/home')}
                    >
                      Home
                    </Button>
                  )}
                  {isAuthenticated && (
                    <Button
                      variant={pathname?.startsWith('/dashboard') ? 'secondary' : 'ghost'}
                      size="sm"
                      onClick={() => router.push('/dashboard/moments')}
                    >
                      Dashboard
                    </Button>
                  )}
                </nav>
              )}
            </div>
            
            {/* Center section - Search bar for dashboard or public search */}
            {showSearch && variant === 'dashboard' && (
              <div className="flex-1 max-w-2xl">
                <DashboardSearchBar />
              </div>
            )}
            {pathname && pathname.startsWith('/home') && !landingMode && variant !== 'dashboard' && (
              <div className="flex-1 max-w-2xl mx-auto hidden md:block">
                <PublicSearchBar showExamples={false} />
              </div>
            )}

            {/* Right section */}
            <div className="flex items-center gap-4">
              {rightContent || (
                <>
                  {landingMode && (
                    <Link
                      href="/start"
                      className="hidden sm:inline-flex bg-foreground text-background px-4 py-1.5 rounded-full text-[13px] font-semibold hover:opacity-90 transition-all"
                    >
                      Get Your Free Booking Page
                    </Link>
                  )}
                  {variant === 'dashboard' && isAuthenticated && (
                    <>
                      <ProcessingStatusBell />
                      <div className="h-5 w-px bg-border" />
                    </>
                  )}
                  <ThemeToggle />
                  
                  {/* User avatar and menu or sign in buttons */}
                  {isAuthenticated ? (
                    <>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className="outline-none focus-visible:ring-2 focus-visible:ring-apprendo-500 focus-visible:ring-offset-1 rounded-full transition-transform hover:scale-105"
                            disabled={isLoading}
                          >
                            <Avatar className="h-8 w-8 cursor-pointer">
                              <AvatarImage 
                                src={profile?.avatar_url || undefined}
                                alt={profile?.name || 'User'}
                              />
                              <AvatarFallback className="bg-apprendo-100 text-apprendo-700 text-xs font-semibold dark:bg-apprendo-900 dark:text-apprendo-200">
                                {getUserInitials()}
                              </AvatarFallback>
                            </Avatar>
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56 mt-1">
                          <DropdownMenuLabel className="font-normal">
                            <div className="flex flex-col space-y-1">
                              {isLoading ? (
                                <>
                                  <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                                  <div className="h-3 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mt-1"></div>
                                </>
                              ) : isAuthenticated && user ? (
                                <>
                                  {/* Optimistic UI showing user info even before full profile loads */}
                                  <p className="text-sm font-medium leading-none">
                                    {profile?.name || (user.email ? user.email.split('@')[0] : 'User')}
                                  </p>
                                  <p className="text-xs leading-none text-muted-foreground">
                                    {profile?.handle ? `@${profile.handle}` : (user.email || '')}
                                  </p>
                                </>
                              ) : (
                                <p className="text-sm font-medium leading-none">Please sign in</p>
                              )}
                            </div>
                          </DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuGroup>
                            {/* Conversation room - domain-context aware */}
                            <DropdownMenuItem onClick={() => {
                              const roomHandle = profile?.handle || user?.email?.split('@')[0] || 'default';
                              const isTeamContext = profile?.active_domain_handle && profile?.active_domain_role && profile.active_domain_role !== 'owner' && profile.active_domain_role !== 'byline';
                              const domainInfo = isTeamContext ? { domain_id: profile!.active_domain_id!, domain_handle: profile!.active_domain_handle!, role: profile!.active_domain_role! } : null;
                              const roomPath = getRoomUrl(roomHandle, domainInfo, profile?.domain_id || null);
                              router.push(roomPath);
                            }}>
                              <MessageCircle className="mr-2 h-4 w-4 text-apprendo-500" />
                              <span>{profile?.active_domain_role && profile.active_domain_role !== 'owner' && profile.active_domain_role !== 'byline' && profile.active_domain_handle ? 'Team Conversation Room' : 'My Conversation Room'}</span>
                              <Sparkles className="ml-auto h-3 w-3 text-apprendo-500" />
                            </DropdownMenuItem>
                            
                            {/* Always show dashboard */}
                            <DropdownMenuItem onClick={() => router.push('/dashboard/moments')}>
                              <LayoutDashboard className="mr-2 h-4 w-4" />
                              <span>Dashboard</span>
                            </DropdownMenuItem>
                            
                            {/* Show admin dashboard if admin */}
                            {adminCheckComplete && isAdmin && (
                              <DropdownMenuItem onClick={() => router.push('/admin')}>
                                <Shield className="mr-2 h-4 w-4 text-amber-600" />
                                <span>Admin Dashboard</span>
                              </DropdownMenuItem>
                            )}
                            
                            {/* Profile - domain-context aware */}
                            <DropdownMenuItem
                              onClick={() => {
                                if (profile?.handle) {
                                  const isTeam = profile.active_domain_handle && profile.active_domain_role && profile.active_domain_role !== 'owner' && profile.active_domain_role !== 'byline';
                                  if (isTeam) {
                                    router.push(`/@${profile.active_domain_handle}/book/${profile.handle}`);
                                  } else {
                                    router.push(`/@${profile.handle}`);
                                  }
                                } else {
                                  router.push('/settings');
                                }
                              }}
                            >
                              <User className="mr-2 h-4 w-4" />
                              <span>{profile?.active_domain_role && profile.active_domain_role !== 'owner' && profile.active_domain_role !== 'byline' && profile.active_domain_handle ? 'Team Profile' : 'Profile'}</span>
                            </DropdownMenuItem>
                            
                            <DropdownMenuItem 
                              onClick={() => {
                                if (profile?.handle) {
                                  router.push(`/@${profile.handle}/edit`);
                                } else {
                                  router.push('/settings');
                                }
                              }}
                            >
                              <Settings className="mr-2 h-4 w-4" />
                              <span>Settings</span>
                            </DropdownMenuItem>
                          </DropdownMenuGroup>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={async () => {
                              console.log('[AppHeader] Logout clicked');
                              await signOut();
                            }} 
                            className="text-red-600"
                          >
                            <LogOut className="mr-2 h-4 w-4" />
                            <span>Log out</span>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </>
                  ) : (
                    <div className="flex items-center gap-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => router.push('/login?signup=false')}
                      >
                        Sign in
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => router.push('/login?signup=true')}
                        className="text-white transition-all bg-apprendo-500 hover:bg-apprendo-600"
                      >
                        Create account
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </header>
      
        {/* Dashboard menu - only shown when showMenu is true */}
        {showMenu && navItems && (
          <DashboardNav items={navItems} />
        )}
      </div>
    </TooltipProvider>
  );
}